// Translation
import { useTranslationContext } from '../../context/TranslationContext';
// Components/ui
import { InputContainer } from '../../layout';
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Popper,
  Paper,
  ClickAwayListener,
  InputAdornment,
  IconButton,
} from '@mui/material';
// Types
import { UseLinkContact } from '../../hooks/useEvent';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { ContactSearchResults } from './components';
import { useLinkContactsSearch } from './hooks/useLinkContactsSearch';
import { useEventsDialogApi } from '../../context/EventsDialogApiContext';
import { useRef, useEffect, useState } from 'react';

type LinkContactsSelectProps = {
  linkContactsHandler: UseLinkContact;
  disabled?: boolean;
};

const LinkContactsSelect = ({ linkContactsHandler, disabled = false }: LinkContactsSelectProps) => {
  const t = useTranslationContext();
  const api = useEventsDialogApi();
  const inputRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  // Estado local para actualización inmediata sin depender de la API
  const [localValue, setLocalValue] = useState('');

  const {
    action: { handleSearchChange, handleSelectContact, handleRemoveContact },
    state: { search, selectedContact },
  } = linkContactsHandler;

  const {
    contactList,
    hasSearched,
    isLoading,
    isLoadingTimer,
    handleQueryChanged,
    handleScroll,
    resetSearchState,
  } = useLinkContactsSearch(api);

  const safeSearch = search || '';
  const showDropdown = Boolean(anchorEl) && safeSearch.length > 0 && !disabled && !selectedContact;

  // Sincronizar localValue con el estado del hook
  // Esto permite que los cambios del backend se reflejen, pero también
  // permite actualización inmediata local
  useEffect(() => {
    if (selectedContact) {
      setLocalValue(selectedContact.name);
    } else {
      setLocalValue(safeSearch);
    }
  }, [selectedContact?.id, selectedContact?.name, safeSearch]);

  // Sincronizar búsqueda con API
  useEffect(() => {
    if (safeSearch) {
      handleQueryChanged(safeSearch);
    } else {
      resetSearchState();
    }
  }, [safeSearch, handleQueryChanged, resetSearchState]);

  const handleInputChange = (value: string) => {
    // Actualizar estado local inmediatamente para feedback instantáneo
    setLocalValue(value);
    
    // Si hay contacto seleccionado y el valor difiere, limpiarlo primero
    if (selectedContact && value !== selectedContact.name) {
      handleRemoveContact();
    }
    
    // Actualizar la búsqueda
    handleSearchChange(value);
    
    if (value && !anchorEl) {
      setAnchorEl(inputRef.current);
    } else if (!value) {
      setAnchorEl(null);
    }
  };

  const handleContactSelect = (contact: any) => {
    // Actualizar estado local inmediatamente
    setLocalValue(contact.name);
    handleSelectContact(contact);
    // handleSelectContact already clears search; close dropdown
    setAnchorEl(null);
    resetSearchState();
  };

  const handleRemove = () => {
    // Limpiar estado local inmediatamente
    setLocalValue('');
    handleRemoveContact();
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  // Modo vista: mostrar el nombre del contacto como texto estático
  if (disabled) {
    return (
      <InputContainer title={t('global.linkContacts')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          {selectedContact && (
            <PersonIcon
              sx={{ opacity: 0.5, color: (theme) => theme.palette.text.secondary }}
              fontSize="small"
            />
          )}
          <Typography
            variant="body1"
            sx={{ color: (theme) => theme.palette.text.secondary, fontSize: '0.9rem' }}
          >
            {localValue || '-'}
          </Typography>
        </Box>
      </InputContainer>
    );
  }

  // Modo edición: campo único — muestra el nombre al seleccionar, búsqueda al escribir
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <InputContainer title={t('global.linkContacts')}>
          <TextField
            ref={inputRef}
            fullWidth
            size="small"
            placeholder={t('global.searchContacts')}
            value={localValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => {
              if (localValue && !selectedContact) {
                setAnchorEl(inputRef.current);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ opacity: 0.5 }} fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: selectedContact ? (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleRemove}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : isLoading || isLoadingTimer ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            }}
          />
        </InputContainer>

        <Popper
          open={showDropdown}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ width: anchorEl?.offsetWidth || 'auto', zIndex: 1300 }}
        >
          <Paper elevation={8} sx={{ mt: 0.5, maxHeight: 300, overflow: 'hidden' }}>
            {hasSearched && contactList.length > 0 && (
              <ContactSearchResults
                handleScroll={(e) => handleScroll(e, safeSearch)}
                contactList={contactList}
                handleSelectContact={handleContactSelect}
                selectedContactIds={[]}
                search={safeSearch}
              />
            )}
            {hasSearched && contactList.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                  {t('global.noResults', 'No se encontraron resultados')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default LinkContactsSelect;
